import { Injectable, inject, signal, computed } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import { UsuarioRepository } from 'src/app/domain/service/usuario/usuario-repository';
import { IdentityServiceImpl } from 'src/app/infrastructure/adapters/security/identity.service';
import { UsuarioSesion, getNombreCorto } from '@model/usuario/usuario-sesion';
import { SESSION_KEY_IDENTITY_DNI, SESSION_KEY_USUARIO, TIPO_EMPLEADO } from 'src/app/common/constants-ejb';
import { environment } from 'src/environments/environment';

/**
 * Tiempo máximo de espera a que IdentityServiceImpl resuelva /user/current
 * (y persista el DNI SSO) antes de pedir la sesión GIROP al backend.
 */
const ESPERA_IDENTIDAD_SSO_MS = 15000;

/**
 * Servicio de Estado: UsuarioStateService
 * 
 * Gestiona el estado del usuario actual de forma centralizada.
 * Persiste en sessionStorage para sobrevivir recargas de página.
 * Expone signals reactivos para acceso en componentes.
 * 
 * Beneficios:
 * - Una sola llamada al backend por sesión del navegador
 * - Estado centralizado y reactivo (signals)
 * - Persistencia en sessionStorage
 * - Computed signals para lógica de negocio (esAsesor, etc.)
 * 
 * @author Sura - Migración GWT a Spring Boot
 * @since 1.0
 */
@Injectable({ providedIn: 'root' })
export class UsuarioStateService {

  private readonly repository = inject(UsuarioRepository);
  /**
   * Misma instancia raíz que usan LayoutComponent y AuthorizationGuard: comparte la
   * petición /user/current en curso. (IdentityRepository solo se provee en el layout
   * y no es visible desde un servicio providedIn: 'root').
   */
  private readonly identity = inject(IdentityServiceImpl);

  /** Estado interno del usuario (signal privado) */
  private readonly _usuario = signal<UsuarioSesion | null>(null);

  /** Estado de carga */
  private readonly _cargando = signal(false);

  /** Carga en curso compartida por llamadores concurrentes (evita llamadas duplicadas) */
  private cargaEnCurso: Promise<UsuarioSesion | null> | null = null;

  // === Signals públicos (solo lectura) ===

  /** Datos del usuario actual */
  readonly usuario = this._usuario.asReadonly();
  
  /** Indica si está cargando datos del backend */
  readonly cargando = this._cargando.asReadonly();

  // === Computed signals para lógica de negocio ===

  /**
   * Indica si el usuario actual es tipo Asesor: su tipo de empleado coincide con el
   * parámetro general CDTIPO_EMP_ASES (respaldo 'A' si el backend no lo envía).
   * Cuando es true, ciertos filtros deben precargarse y bloquearse.
   *
   * @legacy FilterProgramarServiciosViewImpl: getCdtipo().equalsIgnoreCase(
   *         GeneralParameters.getParameterValue(CDTIPO_EMP_ASES))
   */
  readonly esAsesor = computed(() => {
    const usuario = this._usuario();
    const tipoUsuario = (usuario?.cdtipoEmpleado ?? '').trim();
    const tipoAsesor = (usuario?.cdtipoEmpAses ?? '').trim() || TIPO_EMPLEADO.ASESOR;
    return tipoUsuario !== '' && tipoUsuario.toLowerCase() === tipoAsesor.toLowerCase();
  });

  /**
   * Indica si el usuario es Director Técnico.
   *
   * Legacy: AsesoresSearchViewImpl.getDTO() L88-94 — el Director Técnico busca asesores
   * sin filtro de tipo de empleado. Se resuelve en el backend; ante ausencia: false.
   */
  readonly esDirectorTecnico = computed(() => {
    return this._usuario()?.esDirectorTecnico ?? false;
  });

  /**
   * Valor del parámetro general CDTIPO_EMP_ASES (tipo de empleado asesor).
   * Cadena vacía si el backend no lo envía o el parámetro no está configurado.
   */
  readonly cdtipoEmpAses = computed(() => {
    return this._usuario()?.cdtipoEmpAses ?? '';
  });

  /**
   * Nombre corto del usuario para mostrar en UI.
   */
  readonly nombreCorto = computed(() => {
    const usuario = this._usuario();
    return usuario ? getNombreCorto(usuario) : '';
  });

  /**
   * Nombre de la oficina del usuario.
   */
  readonly oficina = computed(() => {
    const usuario = this._usuario();
    return usuario?.nombreOficina || '';
  });

  /**
   * Perfil del usuario.
   */
  readonly perfil = computed(() => {
    const usuario = this._usuario();
    return usuario?.descripcionPerfil || '';
  });

  // === Métodos públicos ===

  /**
   * Carga los datos del usuario.
   * 
   * 1. Intenta recuperar de sessionStorage (persistido)
   * 2. Si no existe, llama al backend y persiste
   * 
   * Es idempotente: múltiples llamadas no generan múltiples requests.
   * 
   * @returns Promise con los datos del usuario
   */
  async cargar(): Promise<UsuarioSesion | null> {
    // Si ya hay usuario cargado, retornarlo
    const usuarioActual = this._usuario();
    if (usuarioActual) {
      return usuarioActual;
    }

    // Evitar llamadas duplicadas: los llamadores concurrentes esperan la misma carga
    if (!this.cargaEnCurso) {
      this.cargaEnCurso = this.ejecutarCarga().finally(() => {
        this.cargaEnCurso = null;
      });
    }
    return this.cargaEnCurso;
  }

  private async ejecutarCarga(): Promise<UsuarioSesion | null> {
    this._cargando.set(true);

    try {
      // 1. Intentar recuperar de sessionStorage
      const cached = this.recuperarDeSession();
      if (cached && this.esSesionCacheValida(cached)) {
        if (!environment.production) {
          console.log('[UsuarioState] Usuario recuperado de sessionStorage');
        }
        this._usuario.set(cached);
        return cached;
      }
      if (cached) {
        this.limpiarSession();
      }

      // 2. Cargar del backend (el header X-User-DNI necesita el DNI SSO ya persistido)
      await this.esperarIdentidadSso();
      if (!environment.production) console.log('[UsuarioState] Cargando usuario desde backend...');
      const usuario = await this.repository.obtenerDatosSesion();

      if (!usuario) {
        this.limpiarSession();
        this._usuario.set(null);
        return null;
      }
      
      // 3. Persistir en sessionStorage
      this.guardarEnSession(usuario);
      this._usuario.set(usuario);
      
      if (!environment.production) {
        console.log('[UsuarioState] Usuario cargado y persistido',
          '- Tipo empleado:', usuario.cdtipoEmpleado,
          '- Oficina:', usuario.nombreOficina);
      }
      
      return usuario;
    } catch (error) {
      console.error('[UsuarioState] Error cargando usuario:', error);
      return null;
    } finally {
      this._cargando.set(false);
    }
  }

  /**
   * Fuerza recarga desde el backend (ignora sessionStorage).
   * Útil si los datos del usuario cambiaron en otro sistema.
   */
  async recargar(): Promise<UsuarioSesion | null> {
    this.limpiarSession();
    this._usuario.set(null);
    this.cargaEnCurso = null;
    return this.cargar();
  }

  /**
   * Limpia los datos del usuario (logout).
   * Elimina de sessionStorage y resetea el signal.
   */
  limpiar(): void {
    this.limpiarSession();
    this._usuario.set(null);
    this.cargaEnCurso = null;
    if (!environment.production) console.log('[UsuarioState] Sesión de usuario limpiada');
  }

  /**
   * Verifica si hay un usuario cargado.
   */
  estaAutenticado(): boolean {
    return this._usuario() !== null;
  }

  /**
   * Obtiene el DNI del usuario autenticado.
   * Si no está cargado aún, lo carga primero.
   * 
   * Utilidad transversal usada por los adapters HTTP que necesitan
   * enviar el DNI como header o parámetro al backend.
   */
  async obtenerDni(): Promise<string> {
    const usuario = this._usuario() ?? await this.cargar();
    if (!usuario?.dni) {
      throw new Error('No fue posible obtener el DNI del usuario autenticado');
    }
    return usuario.dni;
  }

  // === Métodos privados ===

  /**
   * Recupera usuario de sessionStorage.
   */
  private recuperarDeSession(): UsuarioSesion | null {
    try {
      const json = sessionStorage.getItem(SESSION_KEY_USUARIO);
      if (json) {
        return JSON.parse(json) as UsuarioSesion;
      }
    } catch (error) {
      console.warn('[UsuarioState] Error leyendo sessionStorage:', error);
      this.limpiarSession();
    }
    return null;
  }

  /**
   * Evita reutilizar sesión cacheada de otro usuario autenticado, o cacheada por una
   * versión anterior que no traía los permisos derivados (el flag ausente se leería
   * como `false` y restringiría al usuario sin que el backend lo haya decidido).
   */
  private esSesionCacheValida(usuarioCacheado: UsuarioSesion): boolean {
    if (typeof usuarioCacheado?.esDirectorTecnico !== 'boolean') {
      return false;
    }
    const dniIdentidad = sessionStorage.getItem(SESSION_KEY_IDENTITY_DNI);
    if (!dniIdentidad || !usuarioCacheado?.dni) {
      return true;
    }
    return dniIdentidad === usuarioCacheado.dni;
  }

  /**
   * Guarda usuario en sessionStorage.
   */
  private guardarEnSession(usuario: UsuarioSesion): void {
    try {
      sessionStorage.setItem(SESSION_KEY_USUARIO, JSON.stringify(usuario));
    } catch (error) {
      console.warn('[UsuarioState] Error guardando en sessionStorage:', error);
    }
  }

  /**
   * Elimina usuario de sessionStorage.
   */
  private limpiarSession(): void {
    try {
      sessionStorage.removeItem(SESSION_KEY_USUARIO);
    } catch (error) {
      // Ignorar errores de sessionStorage
    }
  }

  /**
   * Espera a que la identidad SSO (/user/current) persista el DNI en sessionStorage.
   *
   * Al entrar por enlace directo (ej. /agenda en pestaña nueva) la pantalla se inicializa
   * en paralelo con el layout que dispara /user/current; sin esta espera el header
   * X-User-DNI no se puede construir y la sesión GIROP queda vacía (sin lupa ni precarga).
   */
  private async esperarIdentidadSso(): Promise<void> {
    if (sessionStorage.getItem(SESSION_KEY_IDENTITY_DNI)) {
      return;
    }
    try {
      await firstValueFrom(
        this.identity.getUserInfo().pipe(timeout(ESPERA_IDENTIDAD_SSO_MS))
      );
    } catch {
      // Sin identidad a tiempo (o /user/current falló): obtenerDatosSesion() falla y cargar() retorna null
    }
  }
}
